import { useQueryClient } from "@tanstack/react-query";

import type { Route } from "./+types/$locale.locations.$id";
import { getLocationBySlug } from "@/api/locations";
import { buildSeoMeta } from "@/components/seo/meta";
import { localBusinessSchema } from "@/components/seo/structuredData";
import { LocationDetailPage } from "@/features/locations/routes/LocationDetailPage";

/**
 * Build-time loader: fetches the location by slug so the route's `meta` export
 * can build LocalBusiness JSON-LD + canonical/OG tags from the same data.
 *
 * `getLocationBySlug` returns null on 404 (it catches the axios error
 * internally), so we translate null → a 404 Response that RR v7's ErrorBoundary
 * can render.
 */
export async function loader({ params }: Route.LoaderArgs) {
    // Prerender sentinel (see react-router.config.ts): resolve without calling
    // the API, and with a 200 — the prerenderer fails the build on ANY non-2xx
    // data response, 404s included. Null renders the shell.
    if ((params.id as string) === "__prerender_sentinel__") {
        return { location: null };
    }

    const location = await getLocationBySlug(params.id as string);
    if (!location) {
        throw new Response("Location not found", { status: 404 });
    }
    return { location };
}

export function meta({ loaderData, params, location: routerLocation }: Route.MetaArgs) {
    if (!loaderData?.location) return [{ title: "Location not found" }];
    const loc = loaderData.location;
    const locale = (params.locale as string) ?? "en";
    const path = routerLocation.pathname.replace(new RegExp(`^/${locale}`), "") || "/";

    return buildSeoMeta({
        title: `${loc.name} — Pickup Location`,
        description: `Visit our ${loc.name} pickup location${loc.city ? ` in ${loc.city}` : ""}. Collect clean, safe premium baby equipment for your stay.`,
        path,
        image: loc.imageUrl ?? undefined,
        imageAlt: loc.name,
        breadcrumbs: [
            { name: "Home", path: "/" },
            { name: "Locations", path: "/locations" },
            { name: loc.name, path },
        ],
        // TODO Phase 6: hydrate availableLocales from root loader manifest
        locale,
        availableLocales: [{ code: "en" }],
        jsonLd: localBusinessSchema(
            {
                name: loc.name,
                path,
                description: loc.description || undefined,
                image: loc.imageUrl || undefined,
                streetAddress: loc.address || undefined,
                addressLocality: loc.city || undefined,
                postalCode: loc.postalCode || undefined,
                addressCountry: loc.country || undefined,
                telephone: loc.phone || undefined,
            },
            locale,
        ),
    });
}

export default function LocationDetailRoute({ loaderData }: Route.ComponentProps) {
    const queryClient = useQueryClient();
    // Seed the TanStack Query cache so useLocationBySlug in LocationDetailPage
    // returns the loader data on first render with no fetch.
    // Key shape mirrors useLocationBySlug in app/hooks/useLocations.ts.
    // (Null on prerender-sentinel paths: seed nothing, render the shell.)
    if (loaderData.location) {
        const slug = loaderData.location.slug ?? loaderData.location.id;
        queryClient.setQueryData(["locations", "by-slug", slug], loaderData.location);
    }
    return <LocationDetailPage />;
}
